import { useEffect, useState } from 'react';
import { QueryResult } from '@apollo/client';
import { LinearProgress, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Equifax_GetConsumerCreditReportInput,
  Equifax_GetConsumerCreditReportPdfInput,
  Equifax_GetConsumerCreditReportPdfQuery,
  Equifax_GetConsumerCreditReportQuery,
  Exact,
  useEquifax_GetConsumerCreditReportLazyQuery,
  useEquifax_GetConsumerCreditReportPdfLazyQuery,
  useEstate_GetEstateByIdQuery,
} from 'queries';

import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

interface Props {
  estateId: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onExitClick: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onPullReportDataComplete: Function;
}

const useStyles = makeStyles(() => ({
  pageContainer: {
    display: 'flex',
    marginBottom: 50,
  },
  textContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  progressContainer: {
    width: '75%',
    marginLeft: '12.5%',
  },
  logo: {
    height: '60px',
    width: '60px',
    margin: 'auto',
    marginTop: '75px',
  },
}));

export default function PullReportData({ estateId, onExitClick, onPullReportDataComplete }: Props) {
  const classes = useStyles();

  const [consumerCreditReportHasError, setConsumerCreditReportHasError] = useState(false);
  const [consumerCreditReportPdfHasError, setConsumerCreditReportPdfHasError] = useState(false);

  // Removed TRU-820
  //const [propertyVerificationHasError, setPropertyVerificationHasError] = useState(false);

  const { data: estateData, loading: estateLoading } = useEstate_GetEstateByIdQuery({
    variables: {
      id: estateId,
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting estate by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [loadConsumerCreditReport, { loading: loadConsumerCreditReportExecuting }] =
    useEquifax_GetConsumerCreditReportLazyQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onError: (e) => {
        console.log(e);
        setConsumerCreditReportHasError(true);
      },
    });

  const [loadConsumerCreditReportPdf] = useEquifax_GetConsumerCreditReportPdfLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: (e) => {
      console.log(e);
      setConsumerCreditReportPdfHasError(true);
    },
  });

  /**
   * Removed TRU-820
  const [loadPropertyVerificationReport] = useEquifax_GetPropertyVerificationReportLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: (e) => {
      console.log(e);
      setPropertyVerificationHasError(true);
    },
  });
  */

  // parse out the street information
  const streetAddressParts: string[] = estateData?.data.estateTargetAddressStreet
    ? estateData?.data.estateTargetAddressStreet.split(' ')
    : [];

  const houseNumber = streetAddressParts.shift();
  const streetType = streetAddressParts.pop();
  const streetName = streetAddressParts.shift();

  const socialNum = estateData?.data.estateTargetSsn || '';

  async function tryLoadingEfxConsumerCreditReport(): Promise<
    QueryResult<
      Equifax_GetConsumerCreditReportQuery,
      Exact<{
        input: Equifax_GetConsumerCreditReportInput;
      }>
    >
  > {
    return loadConsumerCreditReport({
      variables: {
        input: {
          estateId,
          firstName: estateData!.data.estateTargetFirstName,
          lastName: estateData!.data.estateTargetLastName,
          socialNum: socialNum.replace(/\D/g, ''), // remove non-digits from ssn
          houseNumber: houseNumber,
          streetName: streetName,
          streetType: streetType,
          apartmentNumber: '',
          city: estateData!.data.estateTargetAddressCity,
          state: estateData!.data.estateTargetAddressState,
          zip: estateData!.data.estateTargetAddressZip,
        },
      },
    });
  }

  async function tryLoadingEfxConsumerCreditReportPdf(): Promise<
    QueryResult<
      Equifax_GetConsumerCreditReportPdfQuery,
      Exact<{
        input: Equifax_GetConsumerCreditReportPdfInput;
      }>
    >
  > {
    return loadConsumerCreditReportPdf({
      variables: {
        input: {
          estateId,
          firstName: estateData!.data.estateTargetFirstName,
          lastName: estateData!.data.estateTargetLastName,
        },
      },
    });
  }

  /**
   * Removed TRU-820
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tryLoadingEfxPropertyReport = (): Promise<any> => {
    return loadPropertyVerificationReport({
      variables: {
        input: {
          estateId,
          firstName: estateData!.data.estateTargetFirstName || '',
          lastName: estateData!.data.estateTargetLastName || '',
          socialNum: socialNum.replace(/\D/g, ''), // remove non-digits from ssn
        },
      },
    });
  };
  */

  async function tryLoadingEfxReports(): Promise<void> {
    // parse out the street information
    Promise.all([tryLoadingEfxConsumerCreditReport() /*, tryLoadingEfxPropertyReport()*/]).then(
      ([ccrResultData]) => {
        console.log('ccrResultData', ccrResultData);
        if (!ccrResultData || !ccrResultData.data?.data?.ccrDataJson) {
          // Error with APIs responses, notify TSA. Results page will contain "None Reported"
          showAlert({
            message: 'Pulling reports failed - please try again!',
            severity: NotificationSeverity.ERROR,
          });
          setConsumerCreditReportHasError(true);
          return;
        }

        // set a delay to avoid a 409 conflict - the requested resource is not yet available
        setTimeout(async () => {
          await tryLoadingEfxReportPdfs(ccrResultData);
        }, 3000);

        // Removed TRU-820
        // if (!pvResultData || !pvResultData?.data?.data?.pvDataJson) {
        //   setPropertyVerificationHasError(true);
        // }
      },
    );
  }

  async function tryLoadingEfxReportPdfs(
    ccrResultData: QueryResult<
      Equifax_GetConsumerCreditReportQuery,
      Exact<{
        input: Equifax_GetConsumerCreditReportInput;
      }>
    >,
  ): Promise<void> {
    tryLoadingEfxConsumerCreditReportPdf().then((pdfDocumentData) => {
      console.log('pdfDocumentData', pdfDocumentData);
      if (!pdfDocumentData || !pdfDocumentData.data?.data?.id) {
        // Error with APIs responses, notify TSA. Results page will contain "None Reported"
        showAlert({
          message: 'Pulling pdfs failed - please try again!',
          severity: NotificationSeverity.ERROR,
        });
        setConsumerCreditReportPdfHasError(true);
        return;
      }

      showToast({
        message: 'Reports have been pulled to Trustate',
        severity: NotificationSeverity.SUCCESS,
      });
      onPullReportDataComplete(ccrResultData);
      onExitClick();
    });
  }

  useEffect(() => {
    if (estateLoading || !estateData) {
      return;
    }
    tryLoadingEfxReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estateLoading, estateData]);

  return (
    <div
      style={{
        opacity: 1,
        visibility: 'visible',
        display: 'flex',
        height: '100%',
        width: '100%',
      }}
    >
      <div style={{ width: '100%' }}>
        <div className={classes.pageContainer}>
          <img src="/trustate-logo.svg" className={classes.logo} alt="trustate" />
        </div>

        {loadConsumerCreditReportExecuting && (
          <>
            <div className={classes.progressContainer}>
              <LinearProgress />
            </div>
            <div className={classes.textContainer}>
              <Typography variant="body1" style={{ marginTop: 20 }}>
                Accessing Trudata ...
              </Typography>
            </div>
          </>
        )}
        {consumerCreditReportHasError && (
          <div className={classes.textContainer}>
            <Typography variant="body1" style={{ marginTop: 20 }}>
              Error accessing credit report - please close and try again
            </Typography>
          </div>
        )}

        {!loadConsumerCreditReportExecuting && !consumerCreditReportPdfHasError && (
          <>
            <div className={classes.progressContainer}>
              <LinearProgress />
            </div>
            <div className={classes.textContainer}>
              <Typography variant="body1" style={{ marginTop: 20 }}>
                Saving Trudata PDF ...
              </Typography>
            </div>
          </>
        )}
        {consumerCreditReportPdfHasError && (
          <div className={classes.textContainer}>
            <Typography variant="body1" style={{ marginTop: 20 }}>
              Error saving credit report PDF - please close and try again
            </Typography>
          </div>
        )}

        {/**
         * Removed TRU-820
         * propertyVerificationHasError && (
          <div className={classes.textContainer}>
            <Typography variant="body1" style={{ marginTop: 20 }}>
              Error accessing property report - please close and try again
            </Typography>
          </div>
        )*/}
      </div>
    </div>
  );
}
